import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Clock,
  ShieldCheck,
  AlertTriangle,
  User,
  Package,
  Flag,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  TrendingUp,
  Pencil,
  MessageCircle,
  Send,
  Star,
  MoreVertical,
} from 'lucide-react';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { types as sdkTypes } from '../../../util/sdkLoader';
import stripeWordmark from '../../../assets/stripe-wordmark.svg';
import {
  buildConversationMessages,
  sortMessagesByCreatedAtAsc,
} from '../../../util/transactionMessages';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../../util/routes';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../../util/userHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../../util/data';
import { formatMoney } from '../../../util/currency';
import { requireListingImage } from '../../../util/configHelpers';
import { getProcess, resolveLatestProcessName } from '../../../transactions/transaction';

import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../../ducks/stripe.duck';

import { AvatarSmall, AvatarMedium, Page, NamedLink, NamedRedirect, LayoutSingleColumn } from '../../../components';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Badge } from '../../../components/ui/badge';

import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';
import NotFoundPage from '../../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchExistingChatTransaction,
  fetchChatMessages,
  sendChatMessage,
  fetchSellerBuyerChats,
} from '../ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  handleNavigateToMakeOfferPage,
  priceForSchemaMaybe,
} from '../ListingPage.shared';

import ActionBarMaybe from '../Notifications/ActionBar';
import EscrowCard from '../../TransactionPage/TransactionPanel/EscrowCard';
import BuyerPickupCodeCard from '../../../components/BuyerPickupCodeCard/BuyerPickupCodeCard';
import { usePickupCode, useReissuePickup } from '../../../ducks/pickup.duck';
import PickupVerifyCard from '../../../components/PickupVerifyCard/PickupVerifyCard';
import SellerReviews from '../../../components/SellerReviews/SellerReviews';
import CustomListingImageGallery from '../CustomListingImageGallery';
import CustomListingFields from '../CustomListingFields';
import SellerBadges, { getUserMembership } from '../../../components/SellerBadges/SellerBadges';
import NearbyBadge from '../../../components/NearbyBadge/NearbyBadge';
import BoostListingDialog from '../../../components/BoostListingDialog/BoostListingDialog';
import BoostStatusPill from '../../../components/BoostListingDialog/BoostStatusPill';
import ReportDialog from '../../../components/ReportDialog/ReportDialog';
import ShareModal from './ShareModal';
import { selectActiveBoostForListing } from '../../../ducks/boost.duck';
import { formatPublicLocation } from '../../../util/listingLocationDisplay';

const { UUID } = sdkTypes;

const BoostSlot = ({ listingId, listingTitle }) => {
  const activeBoost = useSelector(selectActiveBoostForListing(listingId));
  if (activeBoost) {
    return <BoostStatusPill activeBoost={activeBoost} />;
  }
  return (
    <>
      <BoostListingDialog listingId={listingId} listingTitle={listingTitle}>
        <Button
          variant="outline"
          size="icon"
          className="sm:hidden text-blue-600 border-blue-200 hover:bg-blue-50 h-9 w-9"
        >
          <TrendingUp className="w-4 h-4" />
        </Button>
      </BoostListingDialog>
      <BoostListingDialog listingId={listingId} listingTitle={listingTitle}>
        <Button
          variant="outline"
          size="sm"
          className="hidden sm:flex gap-2 font-heading uppercase tracking-wide text-blue-600 border-blue-200 hover:bg-blue-50"
        >
          <TrendingUp className="w-4 h-4" /> Boost Listing
        </Button>
      </BoostListingDialog>
    </>
  );
};

/**
 * Thin slot that pairs the shared BuyerPickupCodeCard with the pickup-code
 * Redux hooks. The data fetch lives in ListingPage's
 * fetchExistingChatTransactionThunk - by the time this renders the code is
 * already in Redux (or will arrive there in flight).
 */
const BuyerPickupCodeCardSlot = ({ transactionId, className }) => {
  const txId = transactionId?.uuid || '';
  const pickupCode = usePickupCode(txId);
  const pickupReissue = useReissuePickup(txId);
  if (!txId || !pickupCode?.data?.code) return null;
  return (
    <BuyerPickupCodeCard
      className={className}
      code={pickupCode.data.code}
      expiresAt={pickupCode.data.expiresAt}
      reissueInProgress={pickupReissue?.isLoading}
      onReissue={
        pickupReissue
          ? () => pickupReissue.reissue().then(() => pickupCode.refetch())
          : null
      }
    />
  );
};

const CustomListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatTxId, setChatTxId] = useState(null);
  const [buyNowError, setBuyNowError] = useState(null);
  const [buyNowQty, setBuyNowQty] = useState(1);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    existingTransactionId,
    existingTransactionLastTransition,
    existingTransactionProcessName,
    existingTransactionProtectedData,
    existingTransactionCreatedAt,
    existingTransactionLastTransitionedAt,
    existingTransactionMetadata,
    existingTransactionPayinTotal,
    existingTransactionLineItems,
    existingTransactionTransitions,
    fetchExistingTransactionInProgress,
    chatMessages,
    fetchChatMessagesInProgress,
    sendChatMessageInProgress,
    buyerChats,
    unreadSaleTransactionIds,
    fetchBuyerChatsInProgress,
    onFetchExistingChatTransaction,
    onFetchChatMessages,
    onSendChatMessage,
    onFetchSellerBuyerChats,
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };
  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  // Compute these before early returns so hooks below can reference them unconditionally.
  const authorUser = ensureUser(currentListing.author);
  const isOwnListing = !!(currentUser?.id && authorUser.id && currentUser.id.uuid === authorUser.id.uuid);

  // Active tx id: either found in Redux or set locally after first send
  const activeChatTxId = chatTxId || existingTransactionId;

  // On mount: look up any existing transaction this buyer has for this listing
  useEffect(() => {
    if (isAuthenticated && !isOwnListing && currentUser?.id) {
      onFetchExistingChatTransaction(listingId);
    }
  }, [isAuthenticated, currentUser?.id?.uuid, isOwnListing]); // eslint-disable-line react-hooks/exhaustive-deps

  // When existing transaction is found → load messages
  useEffect(() => {
    if (existingTransactionId) {
      onFetchChatMessages(existingTransactionId);
    }
  }, [existingTransactionId?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the chat panel itself to its latest message whenever messages
  // update. Avoid scrollIntoView() because that scrolls the whole document
  // and jumps the listing page when arriving from an inbox link.
  useEffect(() => {
    const endEl = chatEndRef.current;
    if (!endEl) return;
    const container = endEl.parentElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [chatMessages.length]);

  // When seller views own listing → load buyer chats
  useEffect(() => {
    if (isOwnListing && currentUser?.id) {
      onFetchSellerBuyerChats(listingId);
    }
  }, [isOwnListing, currentUser?.id?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const isApproved = currentListing.id && currentListing.attributes.state !== 'pendingApproval';
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  if ((isPendingApprovalVariant && isApproved) || pendingOtherUsersListing) {
    return <NamedRedirect name="ListingPage" params={{ id: rawParams.id, slug: listingSlug }} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    title = '',
    price,
    publicData = {},
    description = '',
    metadata = {},
    geolocation,
  } = currentListing.attributes;
  const { category, condition, materialTypes } = publicData;
  const authorDisplayName = userDisplayNameAsString(authorUser, '');
  const { formattedPrice } = priceData(price, config.currency, intl);
  const isMultiStockListing = publicData?.listingType === 'sell-multiple-items';

  // Author metadata: reviewAvg, reviewCount, createdAt
  const authorProfile = authorUser?.attributes?.profile || {};
  const authorMeta = authorProfile?.metadata || {};
  const reviewAvg = authorMeta.reviewAvg || metadata.reviewAvg || null;
  const reviewCount = authorMeta.reviewCount || metadata.reviewCount || 0;
  const listingReviewAvg = metadata.reviewAvg ?? null;
  const listingReviewCount = metadata.reviewCount ?? 0;
  const authorCreatedAt = authorUser?.attributes?.createdAt;
  const joinedDate = authorCreatedAt
    ? new Date(authorCreatedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;
  const { tier: authorMembershipTier, isVerified } = getUserMembership(authorUser);

  const showListingImage =
    config.layout.listingPage === 'coverPhoto' ? false : requireListingImage(config);

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && !currentUser?.attributes?.stripeConnected;

  const handleChatSend = () => {
    const message = chatMessage.trim();
    if (!message) return;
    setChatMessage('');
    if (activeChatTxId) {
      onSendChatMessage(activeChatTxId, message).catch(() => {});
    } else {
      const listing = getListing(listingId);
      onSendInquiry(listing, message)
        .then(txId => {
          setChatTxId(txId);
          onFetchExistingChatTransaction(listingId);
          onFetchChatMessages(txId);
        })
        .catch(() => {});
    }
  };

  // Derive the existing transaction's process state so we can conditionally show EscrowCard.
  const existingTxProcessState = (() => {
    if (!existingTransactionId || !existingTransactionLastTransition || !existingTransactionProcessName) {
      return null;
    }
    try {
      const procName = resolveLatestProcessName(existingTransactionProcessName);
      const process = getProcess(procName);
      return process
        ? process.getState({ attributes: { lastTransition: existingTransactionLastTransition } })
        : null;
    } catch (_e) {
      return null;
    }
  })();
  const existingTxIsPurchased = existingTxProcessState === 'purchased';
  const existingTxIsAwaitingPickup =
    existingTxProcessState === 'purchased' ||
    existingTxProcessState === 'preauthorized' ||
    existingTxProcessState === 'captured-after-inspection-expiry';
  // Terminal states are the only ones where it makes sense to start a fresh tx
  // from the listing page CTAs. Anything else means the buyer already has an
  // open conversation/offer/order in flight on this listing.
  const TERMINAL_TX_STATES = [
    'offer-rejected',
    'payment-expired',
    'canceled',
    'cancelled',
    'reviewed',
    'reviewed-by-customer',
    'reviewed-by-provider',
    'delivered',
    'received',
    'completed',
    'inquiry',
  ];
  const existingTxIsActive =
    !!existingTransactionId &&
    existingTxProcessState &&
    !TERMINAL_TX_STATES.includes(existingTxProcessState);

  const isChatExpanded = chatExpanded;

  const openChatAndFocus = e => {
    e?.currentTarget?.blur();
    requestAnimationFrame(() => {
      chatContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => chatInputRef.current?.focus(), 300);
    });
  };

  // Buy Now CTA mode:
  // - sellerOfferOpen: seller has a live offer (offer-pending/update-pending) and
  //   the buyer can accept it. CTA: "Accept offer ($X) & buy". Repoints the
  //   checkout to request-payment-to-accept-offer on the existing tx.
  // - buyerWaitingOnSeller: buyer made/countered an offer (initial-offer-pending,
  //   customer-offer-pending). CTA: "Buy at list price". New tx at list price.
  // - default: plain "Buy Now" at list price.
  const buyNowCtaMode =
    existingTxIsActive && ['offer-pending', 'update-pending'].includes(existingTxProcessState)
      ? 'sellerOfferOpen'
      : existingTxIsActive &&
        ['customer-initial-offer-pending', 'customer-offer-pending'].includes(existingTxProcessState)
      ? 'buyerWaitingOnSeller'
      : 'default';
  const offers = existingTransactionMetadata?.offers || [];
  const lastOfferSubunits = offers[offers.length - 1]?.offerInSubunits;
  const offerCurrencyForCta = existingTransactionPayinTotal?.currency || config.currency;
  const formattedOfferAmount =
    lastOfferSubunits != null && offerCurrencyForCta
      ? intl.formatNumber(lastOfferSubunits / 100, {
          style: 'currency',
          currency: offerCurrencyForCta,
        })
      : null;
  const buyNowCtaLabel =
    buyNowCtaMode === 'sellerOfferOpen'
      ? `Accept Offer${formattedOfferAmount ? ` ${formattedOfferAmount}` : ''}`
      : buyNowCtaMode === 'buyerWaitingOnSeller'
      ? `Buy at full price${formattedPrice ? ` ${formattedPrice}` : ''}`
      : 'Buy Now';

  const offerReductionPct = Number(process.env.REACT_APP_OFFER_REDUCTION_PCT) || 10;
  const offerCtaPctView =
    String(process.env.REACT_APP_OFFER_CTA_PCT_VIEW ?? 'true').toLowerCase() !== 'false';
  const offerCtaLabel = (() => {
    if (offerCtaPctView || !price?.amount || !price?.currency) {
      return `Offer ${offerReductionPct}% less`;
    }
    const reducedDollars = Math.round((price.amount * (1 - offerReductionPct / 100)) / 100);
    return `Offer ${intl.formatNumber(reducedDollars, {
      style: 'currency',
      currency: price.currency,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    })}`;
  })();

  const handleBuyNowClick = () => {
    setBuyNowError(null);
    try {
      const { pickupEnabled: pu, shippingEnabled: sh } = publicData;
      const deliveryMethod = pu ? 'pickup' : sh ? 'shipping' : undefined;
      if (
        (buyNowCtaMode === 'sellerOfferOpen' || buyNowCtaMode === 'buyerWaitingOnSeller') &&
        existingTransactionId
      ) {
        // Preserve the quantity from the existing tx instead of hardcoding 1
        // (multi-unit listings like pallets-of-bricks would otherwise have
        // their negotiated quantity quietly reset to 1 on buy-at-list).
        const itemLine = existingTransactionLineItems?.find(
          li => li.code === 'line-item/item' && !li.reversal
        );
        const existingQuantity =
          (itemLine?.quantity?.toNumber ? itemLine.quantity.toNumber() : itemLine?.quantity) || 1;
        const existingDeliveryMethod =
          existingTransactionProtectedData?.deliveryMethod || (deliveryMethod || 'pickup');
        const existingTxStub = {
          id: existingTransactionId,
          type: 'transaction',
          attributes: {
            state: `state/${existingTxProcessState}`,
            lastTransition: existingTransactionLastTransition,
            lastTransitionedAt: existingTransactionLastTransitionedAt
              ? new Date(existingTransactionLastTransitionedAt)
              : null,
            createdAt: existingTransactionCreatedAt
              ? new Date(existingTransactionCreatedAt)
              : null,
            processName: existingTransactionProcessName,
            protectedData: existingTransactionProtectedData || {},
            metadata: existingTransactionMetadata || {},
            payinTotal: existingTransactionPayinTotal,
          },
        };
        const initialValues = {
          listing: currentListing,
          transaction: existingTxStub,
          orderData: {
            quantity: existingQuantity,
            deliveryMethod: existingDeliveryMethod,
          },
          confirmPaymentError: null,
        };
        const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);
        callSetInitialValues(setInitialValues, initialValues, !currentUser);
        onInitializeCardPaymentData();
        history.push(
          createResourceLocatorString(
            'CheckoutPage',
            routeConfiguration,
            { id: currentListing.id.uuid, slug: createSlug(currentListing.attributes.title) },
            {}
          )
        );
        return;
      }
      const submitFn = handleSubmit({
        currentUser,
        callSetInitialValues,
        getListing,
        params,
        history,
        routes: routeConfiguration,
        onInitializeCardPaymentData,
      });
      const qty = isMultiStockListing
        ? Math.min(Math.max(1, Number.parseInt(buyNowQty, 10) || 1), currentStock || 1)
        : 1;
      submitFn({
        quantity: qty,
        ...(deliveryMethod ? { deliveryMethod } : {}),
      });
    } catch (e) {
      setBuyNowError('This listing is not available for purchase at this time.');
    }
  };

  const conversationMessages = buildConversationMessages({
    protectedData: existingTransactionProtectedData,
    customer: currentUser,
    provider: authorUser,
    txCreatedAt: existingTransactionCreatedAt,
    lastTransitionedAt: existingTransactionLastTransitionedAt,
    transitions: existingTransactionTransitions,
    realMessages: chatMessages,
  });
  // Latest activity-feed-style transition line shown above the conversation
  // so the buyer always sees the current state of their tx with the seller.
  const latestTransitionMessage = (() => {
    if (!existingTxIsActive || !existingTransactionLastTransition) return null;
    const transitionKey = existingTransactionLastTransition.replace('transition/', '');
    if (transitionKey === 'inquire') return null;
    const procName = resolveLatestProcessName(existingTransactionProcessName) || existingTransactionProcessName;
    const isCustomerActor = /(^|\/)customer-/.test(existingTransactionLastTransition)
      || ['inquire', 'request-payment', 'request-payment-after-inquiry', 'confirm-payment', 'mark-received', 'dispute'].includes(transitionKey);
    // Resolve the offer amount from the last entry in metadata.offers (the offer
    // amount tied to the most recent offer-bearing transition).
    const offers = existingTransactionMetadata?.offers || [];
    const lastOffer = offers[offers.length - 1];
    const offerCurrency = existingTransactionPayinTotal?.currency || config.currency;
    const negotiationOffer =
      lastOffer?.offerInSubunits != null && offerCurrency
        ? intl.formatNumber(lastOffer.offerInSubunits / 100, {
            style: 'currency',
            currency: offerCurrency,
          })
        : '-';
    const translationId = `TransactionPage.ActivityFeed.${procName}.transition.${transitionKey}`;
    const text = intl.formatMessage(
      { id: translationId, defaultMessage: '' },
      {
        actor: isCustomerActor ? 'you' : 'other',
        otherUsersName: authorDisplayName,
        listingTitle: currentListing?.attributes?.title || '',
        negotiationOffer,
        stateStatus: 'current',
      }
    );
    if (!text) return null;
    const transitionedAt = existingTransactionLastTransitionedAt
      ? new Date(existingTransactionLastTransitionedAt)
      : null;
    return {
      id: { uuid: 'tx-activity' },
      sender: null,
      attributes: { content: text, createdAt: transitionedAt },
      isSystem: true,
    };
  })();
  const mergedChatMessages = sortMessagesByCreatedAtAsc([
    ...(latestTransitionMessage ? [latestTransitionMessage] : []),
    ...conversationMessages,
  ]);

  const onContactUser = handleContactUser({
    currentUser,
    getListing,
    params,
    callSetInitialValues,
    location,
    history,
    routes: routeConfiguration,
    setInquiryModalOpen,
  });
  const onSubmitInquiry = values => {
    const listingId = new UUID(rawParams.id);
    const listing = getListing(listingId);
    const { message } = values;
    onSendInquiry(listing, message.trim())
      .then(txId => {
        setInquiryModalOpen(false);
        history.push(`/inbox/${txId.uuid}`);
      })
      .catch(() => {});
  };

  const ogImageUrl = `${config.marketplaceRootURL}/og/listing/${listingId.uuid}`;
  const facebookImages = [{ name: 'facebook', url: ogImageUrl, width: 1200, height: 630 }];
  const twitterImages = [{ name: 'twitter', url: ogImageUrl, width: 1200, height: 630 }];
  const schemaImages = [
    ogImageUrl,
    ...listingImages(currentListing, `${config.layout.listingImage.variantPrefix}-2x`).map(i => i.url),
  ];
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const buyNowQtyNumber = Number.parseInt(buyNowQty, 10);
  const buyNowQtyValid = isMultiStockListing
    ? Number.isFinite(buyNowQtyNumber) && buyNowQtyNumber >= 1 && buyNowQtyNumber <= currentStock
    : true;

  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName: config.marketplaceName }
  );

  // Format condition label: handle snake_case, camelCase, and direct values
  const CONDITION_LABELS = {
    leftover: 'Leftover / Surplus',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    non_working: 'Non-working',
    // legacy values
    'non-working': 'Non-working',
    used_like_new: 'Like New',
    used_good: 'Used - Good',
    used_fair: 'Used - Fair',
  };
  const conditionLabel = condition
    ? CONDITION_LABELS[condition] ||
      condition.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : null;

  const shareLocation = formatPublicLocation(publicData?.location) || null;
  const isCoarseDevice =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareText = [title, formattedPrice].filter(Boolean).join(' · ');
    if (isCoarseDevice && typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return;
      } catch (_e) {
        // fall through to modal
      }
    }
    setShareModalOpen(true);
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...(schemaAvailability ? { availability: schemaAvailability } : {}),
        },
      }}
    >
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        <div className="flex-grow container mx-auto px-4 pt-6 pb-24 lg:pb-8">
          {/* Action bar for own listings */}
          {mounted && currentListing.id && (
            <ActionBarMaybe
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* ── Left column: gallery + info + banners + reviews ── */}
            <div className="lg:col-span-2 space-y-6">

            {/* Image Gallery */}
            {showListingImage && (
              <CustomListingImageGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )}

            {/* Listing Info */}
            <div className="bg-background border border-border rounded-xl p-4 sm:p-6 shadow-sm">
              {/* Title/badges (left) + Price/actions (right) */}
              <div className="flex items-start justify-between gap-4 mb-6">
                {/* Left: title + meta */}
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold uppercase tracking-tight mb-3">
                    {title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-sm text-muted-foreground">
                    <NearbyBadge geolocation={geolocation} publicData={publicData} />
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" /> Listed recently
                    </span>
                    {conditionLabel && (
                      <span className="flex items-center">
                        <Package className="w-4 h-4 mr-1" /> {conditionLabel}
                      </span>
                    )}
                    {listingReviewAvg != null && listingReviewCount > 0 && (
                      <span className="flex items-center">
                        <Star className="w-4 h-4 mr-1 text-amber-400" fill="#fbbf24" />
                        <span className="font-semibold text-foreground">
                          {Number(listingReviewAvg).toFixed(1)}
                        </span>
                        <span className="ml-1">
                          ({listingReviewCount} review{listingReviewCount !== 1 ? 's' : ''})
                        </span>
                      </span>
                    )}
                    {currentStock > 0 && (
                      <span className="flex items-center text-primary bg-primary/10 px-2 py-0.5 rounded font-bold text-[10px]">
                        {currentStock} Available
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: price + action buttons below */}
                <div className="flex flex-col items-end gap-3 shrink-0">
                  {formattedPrice && (
                    <div className="flex flex-col items-end leading-tight">
                      <div className="text-3xl font-bold text-primary">{formattedPrice}</div>
                      {isMultiStockListing ? (
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          per unit
                        </div>
                      ) : null}
                    </div>
                  )}
                  <div className="flex items-center gap-1 sm:gap-2">
                    {isOwnListing && (
                      <>
                        <NamedLink
                          name="EditListingPage"
                          params={{
                            id: listingId.uuid,
                            slug: listingSlug,
                            type: listingPathParamType,
                            tab: listingTab,
                          }}
                          className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                        >
                          <Pencil className="h-4 w-4" />
                        </NamedLink>
                        <NamedLink
                          name="EditListingPage"
                          params={{
                            id: listingId.uuid,
                            slug: listingSlug,
                            type: listingPathParamType,
                            tab: listingTab,
                          }}
                          className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background text-sm font-heading uppercase tracking-wide hover:bg-accent hover:text-accent-foreground font-medium"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Edit Listing
                        </NamedLink>
                        <BoostSlot listingId={listingId.uuid} listingTitle={title} />
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleShare}
                      className="sm:hidden h-9 w-9"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShare}
                      className="hidden sm:flex gap-2 font-heading uppercase tracking-wide"
                    >
                      <Share2 className="w-4 h-4" /> Share
                    </Button>

                    {!isOwnListing && currentUser && authorUser.id && (
                      <button
                        type="button"
                        onPointerUp={e => e.currentTarget.blur()}
                        onClick={openChatAndFocus}
                        className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background text-sm font-heading font-medium uppercase tracking-wide"
                      >
                        <MessageCircle className="w-4 h-4" /> Chat
                      </button>
                    )}

                    {!isOwnListing && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="h-9 w-9" aria-label="More actions">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <ReportDialog targetType="listing" targetId={listingId.uuid} targetName={title}>
                            <DropdownMenuItem onSelect={e => e.preventDefault()} className="cursor-pointer">
                              <Flag className="w-4 h-4 mr-2" />
                              Report listing
                            </DropdownMenuItem>
                          </ReportDialog>
                          <ReportDialog
                            targetType="user"
                            targetId={authorUser.id?.uuid}
                            targetName={authorDisplayName}
                          >
                            <DropdownMenuItem onSelect={e => e.preventDefault()} className="cursor-pointer">
                              <User className="w-4 h-4 mr-2" />
                              Report user
                            </DropdownMenuItem>
                          </ReportDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>

              {/* Material Types */}
              {materialTypes && materialTypes.length > 0 && (
                <div className="border-t border-border pt-6">
                  <h3 className="text-sm font-heading font-bold uppercase mb-3 text-muted-foreground">
                    Material Types
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {materialTypes.map((type, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-secondary/50 font-medium">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {description && (
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-lg font-heading font-bold uppercase mb-3">Description</h3>
                  <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">
                    {description}
                  </p>
                </div>
              )}

              {/* Custom listing fields */}
              <div className="border-t border-border pt-6 mt-6">
                <CustomListingFields
                  publicData={publicData}
                  metadata={metadata}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                  intl={intl}
                />
              </div>

            </div>

            {/* Safety Banner */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-bold text-amber-700 uppercase text-sm mb-1">
                  Safety &amp; Security Notice
                </h4>
                <p className="text-sm text-amber-700/80">
                  Always inspect materials in person before finalizing acceptance. Do not share
                  personal contact information outside the platform until payment is secured.
                </p>
              </div>
            </div>

            {/* Boost Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-bold text-blue-800 uppercase text-sm mb-1">
                  Boost Your Listings
                </h4>
                <p className="text-sm text-blue-700/80">
                  Pro members get 5 of these free per month. Boosted Listings last 24 hours, are
                  shown to more people who are searching for that item or similar items.
                </p>
              </div>
            </div>

            {/* Seller Reviews */}
            <div id="seller-reviews">
              <SellerReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            </div>

            </div>{/* end left column */}

            {/* ── Right column: seller card, sticky ── */}
            <div className="space-y-6 lg:self-start lg:sticky lg:top-24">
              <div className="bg-background border border-border rounded-xl p-4 sm:p-6 shadow-sm">
                <div className="mb-6">
                  <div className="flex items-start gap-4">
                    <NamedLink
                      name="ProfilePage"
                      params={{ id: authorUser.id?.uuid }}
                      style={{ textDecoration: 'none' }}
                      className="shrink-0 hover:opacity-80 transition-opacity"
                    >
                      <AvatarMedium user={authorUser} disableProfileLink />
                    </NamedLink>
                    <div className="min-w-0 pt-0.5">
                      <NamedLink
                        name="ProfilePage"
                        params={{ id: authorUser.id?.uuid }}
                        style={{ textDecoration: 'none' }}
                        className="font-bold text-base leading-tight text-foreground hover:text-primary transition-colors"
                      >
                        {authorDisplayName}
                      </NamedLink>
                      <div className="mt-1">
                        <SellerBadges
                          membershipTier={authorMembershipTier}
                          isVerified={isVerified}
                          size="compact"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          document
                            .getElementById('seller-reviews')
                            ?.scrollIntoView({ behavior: 'smooth' })
                        }
                        className="mt-1.5 inline-flex items-center gap-1.5 text-sm cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80 transition-opacity"
                      >
                        <Star className="w-4 h-4 text-amber-400" fill="#fbbf24" />
                        {reviewAvg != null ? (
                          <>
                            <span className="font-bold text-foreground">
                              {Number(reviewAvg).toFixed(1)}
                            </span>
                            <span className="text-muted-foreground">({reviewCount})</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">No reviews yet</span>
                        )}
                      </button>
                      {joinedDate && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Joined {joinedDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Buy Now + Make Offer buttons
                    Only render once both currentUser is resolved AND we have confirmed
                    the listing author is loaded: prevents a flash of Buy Now on own listings
                    while author data is still in-flight. */}
                {currentUser && authorUser.id && !isOwnListing && (
                  <div className="hidden lg:flex flex-col gap-3 mb-4">
                    {formattedPrice && (
                      <div className="flex items-baseline justify-between border-t border-border pt-4">
                        <span className="text-xs font-heading font-bold uppercase tracking-wide text-muted-foreground">
                          {isMultiStockListing ? 'Unit price' : 'Listing price'}
                        </span>
                        <span className="text-2xl font-bold text-primary">{formattedPrice}</span>
                      </div>
                    )}
                    {isMultiStockListing && currentStock > 0 && (
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="buy-now-qty"
                          className="text-xs font-heading font-bold uppercase tracking-wide text-muted-foreground"
                        >
                          Quantity (max {currentStock})
                        </label>
                        <input
                          id="buy-now-qty"
                          type="number"
                          min={1}
                          max={currentStock}
                          step={1}
                          value={buyNowQty}
                          onChange={e => setBuyNowQty(e.target.value)}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        {!buyNowQtyValid && (
                          <span className="text-xs text-destructive">
                            Enter a quantity between 1 and {currentStock}.
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleBuyNowClick}
                      disabled={currentStock === 0 || !buyNowQtyValid}
                      className="w-full py-3.5 rounded-lg font-heading font-bold uppercase text-base tracking-wide text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {currentStock === 0 ? 'Out of Stock' : buyNowCtaLabel}
                    </button>
                    {!existingTxIsActive && (
                      <button
                        type="button"
                        onClick={handleNavigateToMakeOfferPage({
                          getListing,
                          params,
                          history,
                          routes: routeConfiguration,
                          searchParams:
                            isMultiStockListing && buyNowQtyValid
                              ? { quantity: Number(buyNowQty) }
                              : {},
                        })}
                        disabled={isMultiStockListing && !buyNowQtyValid}
                        className="w-full py-3.5 rounded-lg font-heading font-bold uppercase text-base tracking-wide text-foreground bg-background border-2 border-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {offerCtaLabel}
                      </button>
                    )}
                    {buyNowError && (
                      <p className="text-sm text-destructive text-center">{buyNowError}</p>
                    )}
                  </div>
                )}

                {/* Buyer pickup-code card: shown whenever an active tx is in
                    a pickup-eligible state (default-purchase 'purchased' OR
                    chat-offer-buy 'preauthorized' / 'captured-after-inspection-expiry'). */}
                {!isOwnListing && existingTxIsAwaitingPickup && (
                  <BuyerPickupCodeCardSlot
                    className="mb-4"
                    transactionId={existingTransactionId}
                  />
                )}

                {isOwnListing && existingTxIsPurchased && (
                  <div className="mb-4">
                    <PickupVerifyCard transactionId={existingTransactionId?.uuid} />
                  </div>
                )}

                {/* Secure payment badge: buyers without an active purchase */}
                {!isOwnListing && !existingTxIsPurchased && (
                  <div className="flex items-center justify-center gap-1.5 mb-4 text-xs text-muted-foreground">
                    <ShieldCheck className="w-3 h-3" />
                    <span>Secure payment by</span>
                    <img src={stripeWordmark} alt="Stripe" className="h-3.5 w-auto" />
                  </div>
                )}

                {/* Live Chat: buyer view (inline message history) */}
                {!isOwnListing && (
                  <div
                    ref={chatContainerRef}
                    className={`border border-border rounded-lg overflow-hidden flex flex-col ${isChatExpanded ? 'h-[400px]' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => setChatExpanded(prev => !prev)}
                      className="bg-muted px-4 py-3 border-b border-border flex justify-between items-center w-full text-left hover:bg-muted/80 transition-colors"
                    >
                      <h4 className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Live Chat
                      </h4>
                      <div className="flex items-center gap-3">
                        {existingTransactionId?.uuid ? (
                          <NamedLink
                            name="InboxPageWithThread"
                            params={{ txId: existingTransactionId.uuid }}
                            onClick={e => e.stopPropagation()}
                            className="text-xs font-heading font-bold uppercase tracking-wide text-primary hover:underline"
                          >
                            View order
                          </NamedLink>
                        ) : null}
                        <span className="text-muted-foreground text-xs">{isChatExpanded ? '▾' : '▸'}</span>
                      </div>
                    </button>
                    {!isAuthenticated ? (
                      <div className="flex-grow flex items-center justify-center p-4">
                        <button
                          type="button"
                          onClick={() => history.push('/login')}
                          className="text-primary text-sm underline"
                        >
                          Sign in to chat with the seller
                        </button>
                      </div>
                    ) : (
                      <>
                        {isChatExpanded && (
                        <div className="flex-1 min-h-0 p-4 overflow-y-auto bg-background flex flex-col gap-3">
                          {fetchChatMessagesInProgress || fetchExistingTransactionInProgress ? (
                            <div className="flex items-center justify-center h-full">
                              <span className="text-sm text-muted-foreground">Loading…</span>
                            </div>
                          ) : mergedChatMessages.length === 0 ? (
                            <div className="flex items-center justify-center h-full px-4">
                              <p className="text-sm text-muted-foreground text-center">
                                Send a message to start a conversation with {authorDisplayName}.
                              </p>
                            </div>
                          ) : (
                            <>
                            <div className="mt-auto" />
                            {mergedChatMessages.map(msg => {
                              if (msg.isSystem) {
                                const sysTime = msg.attributes?.createdAt
                                  ? new Date(msg.attributes.createdAt).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '';
                                return (
                                  <div key={msg.id?.uuid} className="flex flex-col items-center my-2">
                                    <span className="text-xs text-muted-foreground bg-muted/60 rounded-full px-3 py-1">
                                      {msg.attributes?.content}
                                    </span>
                                    {sysTime ? (
                                      <span className="text-[10px] text-muted-foreground mt-1">
                                        {sysTime}
                                      </span>
                                    ) : null}
                                  </div>
                                );
                              }
                              const isFromMe =
                                msg.sender?.id?.uuid === currentUser?.id?.uuid;
                              const timeStr = msg.attributes?.createdAt
                                ? new Date(msg.attributes.createdAt).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '';
                              return (
                                <div
                                  key={msg.id?.uuid}
                                  className={`flex flex-col ${isFromMe ? 'items-end' : 'items-start'}`}
                                >
                                  <div
                                    className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                                      isFromMe
                                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                                        : 'bg-muted text-foreground rounded-bl-sm'
                                    }`}
                                  >
                                    {msg.attributes?.content}
                                  </div>
                                  <span className="text-[10px] text-muted-foreground mt-1 mx-1">
                                    {isFromMe ? 'You' : authorDisplayName} · {timeStr}
                                  </span>
                                </div>
                              );
                            })}
                            </>
                          )}
                          <div ref={chatEndRef} />
                        </div>
                        )}

                        <div className="p-3 bg-muted border-t border-border">
                          <div className="flex gap-2">
                            <input
                              ref={chatInputRef}
                              type="text"
                              value={chatMessage}
                              onChange={e => setChatMessage(e.target.value)}
                              onKeyDown={e => {
                                if (
                                  e.key === 'Enter' &&
                                  chatMessage.trim() &&
                                  !sendChatMessageInProgress &&
                                  !sendInquiryInProgress
                                ) {
                                  handleChatSend();
                                }
                              }}
                              disabled={sendChatMessageInProgress || sendInquiryInProgress}
                              placeholder="Type a message…"
                              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                            />
                            <button
                              type="button"
                              disabled={
                                !chatMessage.trim() ||
                                sendChatMessageInProgress ||
                                sendInquiryInProgress
                              }
                              onClick={handleChatSend}
                              className="shrink-0 w-10 h-10 rounded-lg bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Active Buyer Chats: seller view */}
                {isOwnListing && (
                  <div className="border border-border rounded-lg overflow-hidden flex flex-col">
                    <div className="bg-muted px-4 py-3 border-b border-border flex justify-between items-center">
                      <h4 className="font-heading font-bold uppercase text-sm">
                        Active Buyer Chats
                      </h4>
                      {buyerChats.length > 0 && (
                        <span className="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                          {buyerChats.length} Active
                        </span>
                      )}
                    </div>
                    {fetchBuyerChatsInProgress ? (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        Loading…
                      </div>
                    ) : buyerChats.length === 0 ? (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        No active buyer chats yet.
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {buyerChats.map(chat => {
                          const txUuid = chat.transactionId?.uuid;
                          const isUnread = !!unreadSaleTransactionIds?.some(
                            id => (typeof id === 'string' ? id : id?.uuid) === txUuid
                          );
                          const messagePrefix =
                            chat.lastMessageFrom === 'customer'
                              ? `${chat.customerDisplayName}: `
                              : chat.lastMessageFrom === 'provider'
                              ? 'You: '
                              : '';
                          const customerUser = {
                            id: chat.customerId,
                            type: 'user',
                            attributes: {
                              profile: {
                                displayName: chat.customerDisplayName,
                                abbreviatedName: chat.customerAbbreviatedName,
                              },
                            },
                          };
                          return (
                            <a
                              key={txUuid}
                              href={`/inbox/${txUuid}`}
                              className="block p-4 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2 no-underline"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="relative shrink-0">
                                  <AvatarSmall user={customerUser} disableProfileLink />
                                  {isUnread && (
                                    <span
                                      className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary ring-2 ring-background"
                                      aria-label="Unread"
                                    />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p
                                    className={`text-sm ${
                                      isUnread ? 'font-bold text-foreground' : 'font-medium text-foreground'
                                    }`}
                                  >
                                    {chat.customerDisplayName}
                                  </p>
                                  {chat.lastMessageText && (
                                    <p
                                      className={`text-xs line-clamp-1 ${
                                        isUnread ? 'text-foreground font-medium' : 'text-muted-foreground'
                                      }`}
                                    >
                                      {messagePrefix}
                                      {chat.lastMessageText}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="shrink-0 text-[10px] font-bold uppercase text-primary border border-primary rounded px-2 py-1 whitespace-nowrap">
                                View Chat
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Mobile sticky bottom bar
            Same loading guard as the desktop panel: only show once currentUser is resolved
            AND the listing author is loaded: prevents own-listing flash. */}
        {currentUser && authorUser.id && !isOwnListing && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border px-3 py-2.5 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] lg:hidden">
            <div className="flex items-center gap-2">
              <div className="shrink-0 pr-2 border-r border-border leading-tight">
                <div className="text-base font-bold text-foreground">{formattedPrice}</div>
                {isMultiStockListing ? (
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    per unit
                  </div>
                ) : null}
              </div>
              {isMultiStockListing && currentStock > 0 && (
                <input
                  type="number"
                  min={1}
                  max={currentStock}
                  step={1}
                  value={buyNowQty}
                  onChange={e => setBuyNowQty(e.target.value)}
                  aria-label={`Quantity (max ${currentStock})`}
                  className="w-14 shrink-0 px-2 py-2 rounded-md border border-input bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                />
              )}
              <button
                type="button"
                onClick={handleBuyNowClick}
                disabled={currentStock === 0 || !buyNowQtyValid}
                className="flex-1 min-w-0 py-2.5 px-1 rounded-lg font-heading font-bold uppercase text-xs tracking-wide text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {currentStock === 0 ? 'Out of Stock' : buyNowCtaLabel}
              </button>
              {!existingTxIsActive && (
                <button
                  type="button"
                  onClick={handleNavigateToMakeOfferPage({
                    getListing,
                    params,
                    history,
                    routes: routeConfiguration,
                    searchParams:
                      isMultiStockListing && buyNowQtyValid
                        ? { quantity: Number(buyNowQty) }
                        : {},
                  })}
                  disabled={isMultiStockListing && !buyNowQtyValid}
                  className="flex-1 min-w-0 py-2.5 px-1 rounded-lg font-heading font-bold uppercase text-xs tracking-wide text-foreground bg-background border-2 border-foreground hover:bg-muted transition-colors disabled:opacity-50"
                >
                  {offerCtaLabel}
                </button>
              )}
            </div>
            {buyNowError && (
              <p className="text-sm text-destructive text-center mt-2">{buyNowError}</p>
            )}
          </div>
        )}

        <ShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          url={productURL}
          title={title}
          priceLabel={formattedPrice}
          conditionLabel={conditionLabel}
          location={shareLocation}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

// ── Enhanced wrapper (handles auth redirects) ──
const EnhancedCustomListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;

  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <CustomListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

// ── Redux connect ──
const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };
  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    existingTransactionId: state.ListingPage.existingTransactionId,
    existingTransactionLastTransition: state.ListingPage.existingTransactionLastTransition,
    existingTransactionProcessName: state.ListingPage.existingTransactionProcessName,
    existingTransactionProtectedData: state.ListingPage.existingTransactionProtectedData,
    existingTransactionCreatedAt: state.ListingPage.existingTransactionCreatedAt,
    existingTransactionLastTransitionedAt: state.ListingPage.existingTransactionLastTransitionedAt,
    existingTransactionMetadata: state.ListingPage.existingTransactionMetadata,
    existingTransactionPayinTotal: state.ListingPage.existingTransactionPayinTotal,
    existingTransactionTransitions: state.ListingPage.existingTransactionTransitions,
    existingTransactionLineItems: state.ListingPage.existingTransactionLineItems,
    fetchExistingTransactionInProgress: state.ListingPage.fetchExistingTransactionInProgress,
    chatMessages: state.ListingPage.chatMessages,
    fetchChatMessagesInProgress: state.ListingPage.fetchChatMessagesInProgress,
    sendChatMessageInProgress: state.ListingPage.sendChatMessageInProgress,
    buyerChats: state.ListingPage.buyerChats,
    unreadSaleTransactionIds: state.user.unreadSaleTransactionIds || [],
    fetchBuyerChatsInProgress: state.ListingPage.fetchBuyerChatsInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onFetchExistingChatTransaction: listingId =>
    dispatch(fetchExistingChatTransaction(listingId)),
  onFetchChatMessages: transactionId => dispatch(fetchChatMessages(transactionId)),
  onSendChatMessage: (transactionId, content) =>
    dispatch(sendChatMessage(transactionId, content)),
  onFetchSellerBuyerChats: listingId => dispatch(fetchSellerBuyerChats(listingId)),
});

const CustomListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedCustomListingPage);

export default CustomListingPage;
